import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { Form } from 'new/elements/Form';
import { FormHeaderActions, StepsForm } from 'new/elements/Form/components';
import { ConfigStep } from 'new/elements/flows/ModalConfigureLLM/steps/ConfigStep';
import { SelectConnectionStep } from 'new/elements/flows/ModalConfigureLLM/steps/SelectConnectionStep';
import useLLMProcessor from 'new/elements/flows/ModalConfigureLLM/useLLMProcessor';
import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import { ModalDefault } from 'new/routes/ModalDefault';
import useEntities from 'new/state/entities/repository/useEntities';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { EnterCredentialStep } from 'src/new/elements/flows/ModalConfigureLLM/steps/EnterCredentialStep';
import classes from './ModalConfigureLLM.module.scss';
import { useForm } from 'new/elements/Form/useForm';
import { Stack } from 'new/elements/Stack';

type ModalConfigureLLMProps = {
  data?: any;
  dataSetId?: number;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const config = {
  disableClickOut: true,
};

export const ModalConfigureLLM = (props: ModalConfigureLLMProps) => {
  const { data, dataSetId, open, setOpen } = props;
  const vendors = useEntities('vendors').data;
  const credentials = useEntities('credentials').data;
  const metaInfoById = useSelector((state: any) => state.paging.metaInfoById);
  const { connectionType, updateFormData, resetFormData, startForm, endForm } = useLLMProcessor();

  const start = useRef(true);

  if (start.current && open) {
    startForm();

    const initialData: any = {
      connectionType: 'SINK',
      dataSetId,
    };

    // initialize edit form
    if (data) {
      const connectionOrVendorType = getConnectionOrVendorType(
        data,
        vendors,
        'sink_type',
        'vendor_id',
      );
      const connectionInfo =
        connectionOrVendorType && metaInfoById?.[connectionOrVendorType]
          ? metaInfoById[connectionOrVendorType]
          : null;
      const connectionCategory = connectionInfo?.connectionCategory || null;

      initialData.sink_config = data.template_config;
      initialData.selectedConnection = connectionOrVendorType;
      initialData.selectedCredentialId = data.data_credentials_id;
      initialData.selectedCredential = credentials[initialData.selectedCredentialId];
      initialData.connectionCategory = connectionCategory;
    }

    updateFormData(initialData);
    start.current = false;
  }

  function resetFields() {
    resetFormData([
      'credentialId',
      'selectedCredentialInfo',
      'selectedConnection',
      'selectedBucket',
      'selectedTable',
      'table',
      'source',
      'sink',
      'nexset',
      'isNewTable',
      'sourceConfig',
      'sinkConfig',
    ]);
  }

  useEffect(() => {
    resetFields();
    return () => {
      resetFields();
      if (connectionType === 'SINK') {
        endForm();
      }
    };
  }, []);

  useDidUpdateEffect(() => {
    if (!open) {
      start.current = true;
      endForm();
    }
  }, [open]);

  const onFormDismiss = () => {
    start.current = true;
    endForm();
  };

  const onClose = () => {
    start.current = true;
    setOpen(false);
    endForm();
  };

  return (
    <ModalDefault
      onCancel={onClose}
      config={config}
      rootClassName={classes.modalRootContainer}
      contentClassName={classes.modalContainer}
      renderTitle={({ Title, Container }) => (
        <Container className={classes.titleContainer}>
          <Title>Add LLM Processor</Title>
        </Container>
      )}
      open={open}
    >
      <Form
        containerClassName={classes.formContainer}
        onDismiss={onFormDismiss}
        HeaderComponent={
          <Stack
            padding="12px"
            width="100%"
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap="100px"
          >
            <StepsFormWrapper />
            <FormHeaderActions onCancel={onClose} />
          </Stack>
        }
        description=""
        startPage={!!data?.id ? 2 : undefined}
      >
        <SelectConnectionStep />
        <EnterCredentialStep />
        <ConfigStep data={data} setOpen={setOpen} />
      </Form>
    </ModalDefault>
  );
};

function StepsFormWrapper() {
  const { activePage, steps } = useForm();
  return <StepsForm activePage={activePage} steps={steps as any} />;
}
